import { ChangeEvent } from 'react';
import { NumericFormat, OnValueChange } from 'react-number-format';

import {
  FormFieldControlledComponent,
  FormFieldControlledComponentProps,
} from '../../types/fields';
import { Input, InputProps } from './Input.component';

export type NumberInputProps = InputProps & {
  positiveOnly?: boolean;
  decimalPlaces?: number;
  maxLength?: number;
  thousandSeparator?: string | boolean;
};

export const NumberInput: FormFieldControlledComponent<
  string,
  NumberInputProps
> = ({
  positiveOnly = true,
  decimalPlaces = 0,
  maxLength,
  onChange,
  defaultValue = '',
  thousandSeparator = ',',
  value,
  ...props
}: FormFieldControlledComponentProps<string, NumberInputProps>) => {
  const handleValueChange: OnValueChange = (
    { value: newValue },
    { source },
  ) => {
    // Ignore prop-driven changes (mount/reset); only react to real user input.
    if (source !== 'event') {
      return;
    }

    let modifiedNewValue = newValue;

    if (maxLength && decimalPlaces === 0) {
      modifiedNewValue = modifiedNewValue.slice(0, maxLength);
    }

    if (onChange) {
      const syntheticEvent = {
        target: { value: modifiedNewValue },
      } as unknown as ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  return (
    <Input
      asChild
      id={props.name}
      inputMode={decimalPlaces > 0 ? 'decimal' : 'numeric'}
      min={positiveOnly ? 0 : undefined}
      value={value ?? defaultValue}
      w="full"
      {...props}
    >
      <NumericFormat
        decimalScale={decimalPlaces}
        onValueChange={handleValueChange}
        thousandSeparator={thousandSeparator}
        valueIsNumericString
      />
    </Input>
  );
};

NumberInput.controlled = true;
NumberInput.displayName = 'NumberInput';
